import { useState, useEffect } from "react";
import { images } from "../constants/images";
import { getCaseStudies } from "../api/utils";
import useFetch from "../api/hooks/useFetch";
import "../assets/stylesheets/bootstrap.min.css";
import "../assets/stylesheets/style.css";
import "../assets/stylesheets/responsive.css";

import { CASE_STUDIES } from "../constants/caseStudies";

export default function OurWork() {
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [selectedIndustry, setSelectedIndustry] = useState("All");
  const [projects, setProjects] = useState(CASE_STUDIES);
  const [loading, setLoading] = useState(true);

  // Fetch case studies from API

  const { apiData, apiLoading, error } = useFetch(
    () => getCaseStudies(selectedCategory === "All" ? "" : selectedCategory),
    [selectedCategory], // This should be an array
  );

  useEffect(() => {
    document.title = "Our Work - Custom Software Projects | Plural Code";
    window.scrollTo(0, 0);
  }, []);

  // Service types (categories) matching backend
  const categories = [
    "All",
    "Web Development",
    "Mobile Development",
    "Fullstack Development",
    "Software Audit Report",
    "ERP Development",
  ];

  // Industries matching backend
  const industries = ["All"].concat(CASE_STUDIES.map((cs) => cs.industry));

  // Filter projects by industry (client-side filtering)
  const filteredProjects = projects.filter((project) => {
    const matchesIndustry =
      selectedIndustry === "All" ||
      project.industry === selectedIndustry ||
      project.industry === "Not Available";
    return matchesIndustry;
  });

  // Handle category change - triggers new API call
  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    setLoading(true);
  };

  return (
    <div className="our-work-page">
      {/* HERO SECTION */}
      <section className="work-hero">
        <div className="hero-background">
          <div className="container">
            <div className="hero-content">
              <div className="hero-badge">
                <span className="badge-icon">🚀</span>
                <span className="badge-text">OUR PORTFOLIO</span>
              </div>
              <h1 className="hero-title">
                Real Projects. <span className="highlight">Real Results.</span>
              </h1>
              <p className="hero-subtitle">
                Custom software solutions we've built for businesses across
                manufacturing, healthcare, logistics, retail, and professional
                services—delivering measurable ROI and competitive advantage.
              </p>
              <div className="hero-stats">
                <div className="stat-item">
                  <div className="stat-value">150+</div>
                  <div className="stat-label">Projects Delivered</div>
                </div>
                <div className="stat-item">
                  <div className="stat-value">6</div>
                  <div className="stat-label">Countries Served</div>
                </div>
                <div className="stat-item">
                  <div className="stat-value">12+</div>
                  <div className="stat-label">Industries</div>
                </div>
                <div className="stat-item">
                  <div className="stat-value">95%</div>
                  <div className="stat-label">Client Retention</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FILTER SECTION */}
      <section className="filter-section">
        <div className="container">
          <div className="filter-header">
            <h2>Browse Our Work</h2>
            <p>
              Filter by solution type and industry to see relevant case studies
            </p>
          </div>

          <div className="filter-grid">
            <div className="filter-group">
              <h3 className="filter-title">
                <span className="filter-icon">🔍</span>
                Filter by Solution Type
              </h3>
              <div className="filter-options">
                {categories.map((category, index) => (
                  <button
                    key={index}
                    onClick={() => handleCategoryChange(category)}
                    className={`filter-option ${
                      selectedCategory === category ? "active" : ""
                    }`}
                    disabled={loading}
                  >
                    {category}
                    {selectedCategory === category && (
                      <span className="active-indicator">✓</span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            <div className="filter-group">
              <h3 className="filter-title">
                <span className="filter-icon">🏭</span>
                Filter by Industry
              </h3>
              <div className="filter-options">
                {industries.map((industry, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedIndustry(industry)}
                    className={`filter-option ${
                      selectedIndustry === industry ? "active" : ""
                    }`}
                  >
                    {industry}
                    {selectedIndustry === industry && (
                      <span className="active-indicator">✓</span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="active-filters">
            {selectedCategory !== "All" && (
              <span className="active-filter">
                {selectedCategory}{" "}
                <button onClick={() => handleCategoryChange("All")}>×</button>
              </span>
            )}
            {selectedIndustry !== "All" && (
              <span className="active-filter">
                {selectedIndustry}{" "}
                <button onClick={() => setSelectedIndustry("All")}>×</button>
              </span>
            )}
            {(selectedCategory !== "All" || selectedIndustry !== "All") && (
              <button
                className="clear-filters"
                onClick={() => {
                  handleCategoryChange("All");
                  setSelectedIndustry("All");
                }}
              >
                Clear all filters
              </button>
            )}
          </div>
        </div>
      </section>

      {/* LOADING STATE */}
      {/* {loading && (
        <section className="loading-section">
          <div className="container">
            <div className="loading-spinner">
              <div className="spinner"></div>
              <p>Loading projects...</p>
            </div>
          </div>
        </section>
      )} */}

      {/* ERROR STATE */}
      {error && !loading && (
        <section className="error-section">
          <div className="container">
            <div className="error-message">
              <div className="error-icon">⚠️</div>
              <h3>Unable to load projects</h3>
              <p>Please try again later or contact support.</p>
            </div>
          </div>
        </section>
      )}

      {/* FEATURED PROJECTS */}
      {!loading &&
        !error &&
        selectedCategory === "All" &&
        selectedIndustry === "All" &&
        CASE_STUDIES.filter((cs) => cs.featured).length > 0 && (
          <section className="featured-projects">
            <div className="container">
              <div className="section-header">
                <span className="section-badge">
                  <span className="badge-icon">⭐</span>
                  <span className="badge-text">SHOWCASE</span>
                </span>
                <h2>Featured Case Studies</h2>
                <p>
                  In-depth look at our most impactful custom software projects
                </p>
              </div>

              <div className="featured-grid">
                {CASE_STUDIES.filter((cs) => cs.featured).map(
                  (project, index) => (
                    <div key={project.id} className="featured-card">
                      <div className="featured-image">
                        <img
                          src={require(
                            `../assets/images/case-studies/${project.heroImage}`,
                          )}
                          alt={project.title}
                        />
                        <div className="image-overlay">
                          <div className="project-badges">
                            <span className="project-badge category">
                              {project.category || "Custom Business Software"}
                            </span>
                            <span className="project-badge industry">
                              {project.industry}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="featured-content">
                        <div className="featured-header">
                          <h3>{project.title}</h3>
                          <p className="client-name">
                            {project.client.companyName}
                          </p>
                        </div>

                        <p className="project-excerpt">{project.excerpt}</p>

                        <div className="testimonial-preview">
                          <span className="testimonial-icon">💬</span>
                          <p className="testimonial-text">
                            "{project.clientReview.quote.substring(0, 120)}..."
                          </p>
                        </div>

                        <button
                          onClick={() =>
                            window.open(`/ourworks/${project.slug}`, "_blank")
                          }
                          className="view-case-study"
                        >
                          <span>View Full Case Study</span>
                          <span className="arrow">→</span>
                        </button>
                      </div>
                    </div>
                  ),
                )}
              </div>
            </div>
          </section>
        )}

      {/* ALL PROJECTS GRID */}
      <section className="projects-grid-section">
        <div className="container">
          <div className="section-header">
            <h2>
              {selectedCategory === "All" && selectedIndustry === "All"
                ? "All Projects"
                : "Filtered Projects"}
            </h2>
            <p>
              {selectedCategory !== "All" && `${selectedCategory} • `}
              {selectedIndustry !== "All" && `${selectedIndustry} • `}
              Showing {filteredProjects.length} project
              {filteredProjects.length !== 1 ? "s" : ""}
            </p>
          </div>

          {filteredProjects.length === 0 ? (
            <div className="no-results">
              <div className="no-results-icon">🔍</div>
              <h3>No projects found</h3>
              <p>Try adjusting your filters to see more projects.</p>
              <button
                className="reset-filters"
                onClick={() => {
                  handleCategoryChange("All");
                  setSelectedIndustry("All");
                }}
              >
                Reset All Filters
              </button>
            </div>
          ) : (
            <div className="projects-grid">
              {filteredProjects.map((project) => (
                <div
                  key={project.id}
                  className="project-card"
                  onClick={() =>
                    window.open(`/ourworks/${project.slug}`, "_blank")
                  }
                >
                  <div className="project-image">
                    <img
                      src={require(
                        `../assets/images/case-studies/${project.heroImage}`,
                      )}
                      alt={project.title}
                    />
                    <div className="project-overlay">
                      <div className="project-tags">
                        <span className="project-tag">
                          {project.category || "Custom Business Software"}
                        </span>
                        <span className="project-tag">{project.industry}</span>
                      </div>
                    </div>
                  </div>
                  <div className="project-content">
                    <div className="project-header">
                      <h3>{project.title}</h3>
                      <p className="project-client">
                        {project.client.companyName}
                      </p>
                    </div>

                    <p className="project-description">{project.excerpt}</p>

                    <div className="project-footer">
                      <div className="technologies">
                        {project.projectTechnicalDetails.technologies
                          .slice(0, 3)
                          .map((tech, i) => (
                            <span key={i} className="tech-tag">
                              {tech.name}
                            </span>
                          ))}
                      </div>
                      <span className="view-link">View Case Study →</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* TECHNOLOGIES USED */}
      <section className="technologies-section">
        <div className="container">
          <div className="section-header">
            <span className="section-badge">
              <span className="badge-icon">⚙️</span>
              <span className="badge-text">TECH STACK</span>
            </span>
            <h2>Technologies We Use</h2>
            <p>
              We choose the right technology stack for each project based on
              your specific requirements, scalability needs, and long-term
              goals.
            </p>
          </div>

          <div className="technologies-grid">
            <div className="tech-category">
              <div className="tech-category-header">
                <div className="tech-icon">💻</div>
                <h3>Backend</h3>
              </div>
              <div className="tech-items">
                <span className="tech-item">Ruby on Rails</span>
                <span className="tech-item">Python/Django</span>
                <span className="tech-item">Node.js</span>
                <span className="tech-item">PostgreSQL</span>
              </div>
            </div>

            <div className="tech-category">
              <div className="tech-category-header">
                <div className="tech-icon">🎨</div>
                <h3>Frontend</h3>
              </div>
              <div className="tech-items">
                <span className="tech-item">React.js</span>
                <span className="tech-item">Vue.js</span>
                <span className="tech-item">JavaScript (ES6+)</span>
                <span className="tech-item">Tailwind CSS</span>
              </div>
            </div>

            <div className="tech-category">
              <div className="tech-category-header">
                <div className="tech-icon">📱</div>
                <h3>Mobile</h3>
              </div>
              <div className="tech-items">
                <span className="tech-item">React Native</span>
                <span className="tech-item">iOS (Swift)</span>
                <span className="tech-item">Android (Kotlin)</span>
                <span className="tech-item">Flutter</span>
              </div>
            </div>

            <div className="tech-category">
              <div className="tech-category-header">
                <div className="tech-icon">☁️</div>
                <h3>Infrastructure</h3>
              </div>
              <div className="tech-items">
                <span className="tech-item">AWS</span>
                <span className="tech-item">Docker</span>
                <span className="tech-item">Kubernetes</span>
                <span className="tech-item">CI/CD</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* INDUSTRIES SERVED */}
      {!loading && !error && projects.length > 0 && (
        <section className="industries-section">
          <div className="container">
            <div className="section-header">
              <span className="section-badge">
                <span className="badge-icon">🌍</span>
                <span className="badge-text">INDUSTRIES</span>
              </span>
              <h2>Industries We Serve</h2>
              <p>
                We've delivered custom software solutions across diverse sectors
              </p>
            </div>

            <div className="industries-grid">
              {[
                {
                  icon: "🏭",
                  name: "Manufacturing",
                  count: projects.filter((p) => p.industry === "Manufacturing")
                    .length,
                },
                {
                  icon: "💼",
                  name: "Professional Services",
                  count: projects.filter(
                    (p) => p.industry === "Professional Services",
                  ).length,
                },
                {
                  icon: "🚚",
                  name: "Logistics",
                  count: projects.filter((p) => p.industry === "Logistics")
                    .length,
                },
                {
                  icon: "🏪",
                  name: "Retail",
                  count: projects.filter((p) => p.industry === "Retail").length,
                },
                {
                  icon: "🏥",
                  name: "Healthcare",
                  count: projects.filter((p) => p.industry === "Healthcare")
                    .length,
                },
                {
                  icon: "🍽️",
                  name: "Hospitality",
                  count: projects.filter((p) => p.industry === "Hospitality")
                    .length,
                },
                {
                  icon: "🏠",
                  name: "Real Estate",
                  count: projects.filter((p) => p.industry === "Real Estate")
                    .length,
                },
                {
                  icon: "🏗️",
                  name: "Construction",
                  count: projects.filter((p) => p.industry === "Construction")
                    .length,
                },
              ].map((industry, index) => (
                <div
                  key={index}
                  className="industry-card"
                  onClick={() => setSelectedIndustry(industry.name)}
                >
                  <div className="industry-icon">{industry.icon}</div>
                  <div className="industry-content">
                    <h3>{industry.name}</h3>
                    <div className="project-count">
                      <span className="count-number">{industry.count}</span>
                      <span className="count-label">projects</span>
                    </div>
                  </div>
                  <div className="industry-hover">
                    <span>View Projects →</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* TESTIMONIALS SECTION */}
      {!loading &&
        !error &&
        CASE_STUDIES.filter((cs) => cs.featured).length > 0 && (
          <section className="testimonials-section">
            <div className="container">
              <div className="section-header">
                <span className="section-badge">
                  <span className="badge-icon">💬</span>
                  <span className="badge-text">TESTIMONIALS</span>
                </span>
                <h2>What Our Clients Say</h2>
                <p>
                  Hear directly from businesses we've transformed with custom
                  software
                </p>
              </div>

              <div className="testimonials-grid">
                {CASE_STUDIES.filter((cs) => cs.featured).map((project) => (
                  <div key={project.id} className="testimonial-card">
                    <div className="testimonial-rating">
                      <span className="star">★</span>
                      <span className="star">★</span>
                      <span className="star">★</span>
                      <span className="star">★</span>
                      <span className="star">★</span>
                    </div>
                    <p className="testimonial-text">
                      "{project.clientReview.quote}"
                    </p>
                    <div className="testimonial-author">
                      <div className="author-info">
                        <h4>{project.client.companyName}</h4>
                        <p>{project.client.industry}</p>
                      </div>
                      {/* <div className="project-link">
                        <span>{project.category}</span>
                      </div> */}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

      {/* CTA SECTION */}
      <section className="work-cta">
        <div className="container">
          <div className="cta-content">
            <div className="cta-badge">
              <span className="badge-icon">🚀</span>
              <span className="badge-text">START YOUR PROJECT</span>
            </div>
            <h2>Ready to See What We Can Build for You?</h2>
            <p>
              Let's discuss your business challenges and explore how custom
              software can give you a competitive advantage.
            </p>

            <div className="cta-buttons">
              <button
                onClick={() => window.open("/contact", "_blank")}
                className="cta-primary"
              >
                <span>Schedule Free Consultation</span>
                <span className="cta-arrow">→</span>
              </button>
              <button
                onClick={() => window.open("/services", "_blank")}
                className="cta-secondary"
              >
                <span>Explore Our Services</span>
                <span className="cta-arrow">→</span>
              </button>
            </div>

            <div className="cta-contact">
              <div className="contact-info">
                <div className="contact-item">
                  <span className="contact-icon">📧</span>
                  <div>
                    <div className="contact-label">Email</div>
                    <div className="contact-value">info@pluralcode.com</div>
                  </div>
                </div>
                <div className="contact-item">
                  <span className="contact-icon">📞</span>
                  <div>
                    <div className="contact-label">Phone</div>
                    <div className="contact-value">+91 99589 20231</div>
                  </div>
                </div>
                <div className="contact-item">
                  <span className="contact-icon">🌏</span>
                  <div>
                    <div className="contact-label">Serving</div>
                    <div className="contact-value">
                      businesses of all sizes and industries around the world.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <style jsx>{`
        .our-work-page {
          font-family:
            -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
        }

        /* Hero Section - Matching home page style */
        .work-hero {
          min-height: 90vh;
          display: flex;
          width: 100%;
          justify-content: center;
          align-items: center;
          position: relative;
          overflow: hidden;
          background: linear-gradient(
            135deg,
            #0a2458 0%,
            #1a3b8a 30%,
            #2a4ba8 70%,
            #3a5dc8 100%
          );
        }

        .work-hero::before {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background:
            radial-gradient(
              circle at 20% 80%,
              rgba(255, 215, 0, 0.15) 0%,
              transparent 50%
            ),
            radial-gradient(
              circle at 80% 20%,
              rgba(255, 255, 255, 0.1) 0%,
              transparent 50%
            ),
            radial-gradient(
              circle at 40% 40%,
              rgba(10, 36, 88, 0.2) 0%,
              transparent 50%
            );
        }

        .container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 20px;
        }

        .hero-content {
          max-width: 800px;
          margin: 0 auto;
          text-align: center;
          position: relative;
          z-index: 1;
          padding: 80px 0;
        }

        .hero-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(10px);
          padding: 8px 16px;
          border-radius: 50px;
          margin-bottom: 24px;
          border: 1px solid rgba(255, 255, 255, 0.2);
        }

        .badge-icon {
          font-size: 20px;
        }

        .badge-text {
          font-size: 14px;
          font-weight: 600;
          letter-spacing: 1px;
          color: white;
        }

        .hero-title {
          font-size: clamp(2.5rem, 5vw, 3.5rem);
          font-weight: 700;
          line-height: 1.1;
          margin-bottom: 20px;
          color: white;
          text-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
        }

        .highlight {
          background: linear-gradient(90deg, #ffd700 0%, #ffec8b 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .hero-subtitle {
          font-size: 1.25rem;
          line-height: 1.6;
          color: rgba(255, 255, 255, 0.9);
          max-width: 600px;
          margin: 0 auto 40px;
        }

        .hero-stats {
          display: flex;
          justify-content: center;
          gap: 40px;
          flex-wrap: wrap;
          margin-top: 60px;
        }

        .stat-item {
          text-align: center;
          min-width: 120px;
        }

        .stat-value {
          font-size: 2.5rem;
          font-weight: 700;
          color: #ffd700;
          margin-bottom: 4px;
          display: block;
        }

        .stat-label {
          font-size: 0.875rem;
          color: rgba(255, 255, 255, 0.8);
          text-transform: uppercase;
          letter-spacing: 1px;
          display: block;
        }

        /* Filter Section */
        .filter-section {
          padding: 60px 0;
          background: linear-gradient(180deg, #ffffff 0%, #f8faff 100%);
        }

        .filter-header {
          text-align: center;
          margin-bottom: 40px;
        }

        .filter-header h2 {
          font-size: 2rem;
          color: #0a2458;
          margin-bottom: 8px;
        }

        .filter-header p {
          color: #0a2458;
          font-size: 1.125rem;
          margin: 0;
        }

        .filter-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 40px;
          margin-bottom: 30px;
        }

        @media (max-width: 768px) {
          .filter-grid {
            grid-template-columns: 1fr;
            gap: 30px;
          }
        }

        .filter-group {
          background: white;
          border-radius: 16px;
          padding: 24px;
          box-shadow: 0 4px 20px rgba(10, 36, 88, 0.08);
        }

        .filter-title {
          display: flex;
          align-items: center;
          gap: 10px;
          font-size: 1.25rem;
          color: #0a2458;
          margin-bottom: 20px;
        }

        .filter-icon {
          font-size: 24px;
        }

        .filter-options {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
        }

        .filter-option {
          background: #f8fafc;
          border: 2px solid #e2e8f0;
          color: #0a2458;
          padding: 10px 20px;
          border-radius: 50px;
          font-size: 0.875rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .filter-option:hover {
          background: #f0f7ff;
          border-color: #0a2458;
          transform: translateY(-2px);
        }

        .filter-option.active {
          background: linear-gradient(135deg, #0a2458 0%, #1a3b8a 100%);
          color: white;
          border-color: #0a2458;
          font-weight: 600;
        }

        .active-indicator {
          font-size: 12px;
          font-weight: bold;
        }

        .active-filters {
          display: flex;
          align-items: center;
          gap: 12px;
          flex-wrap: wrap;
          padding-top: 20px;
          border-top: 1px solid #e2e8f0;
        }

        .active-filter {
          background: #e6f0ff;
          color: #0a2458;
          padding: 8px 16px;
          border-radius: 20px;
          font-size: 0.875rem;
          font-weight: 500;
          display: inline-flex;
          align-items: center;
          gap: 8px;
        }

        .active-filter button {
          background: none;
          border: none;
          color: #666;
          cursor: pointer;
          font-size: 16px;
          padding: 0;
          width: 20px;
          height: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
        }

        .active-filter button:hover {
          background: rgba(0, 0, 0, 0.1);
        }

        .clear-filters {
          background: transparent;
          border: 2px solid #0a2458;
          color: #0a2458;
          padding: 8px 16px;
          border-radius: 8px;
          font-size: 0.875rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .clear-filters:hover {
          background: #0a2458;
          color: white;
        }

        /* Section Header */
        .section-header {
          text-align: center;
          margin-bottom: 60px;
        }

        .section-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: rgba(10, 36, 88, 0.1);
          padding: 6px 12px;
          border-radius: 20px;
          margin-bottom: 16px;
        }

        .section-header h2 {
          font-size: 2.5rem;
          color: #0a2458;
          margin-bottom: 12px;
        }

        .section-header p {
          color: #0a2458;
          font-size: 1.125rem;
          max-width: 600px;
          margin: 0 auto;
        }

        /* Featured Projects */
        .featured-projects {
          padding: 80px 0;
          background: linear-gradient(180deg, #ffffff 0%, #f8faff 100%);
        }

        .featured-grid {
          display: flex;
          flex-direction: column;
          gap: 60px;
        }

        .featured-card {
          background: white;
          border-radius: 20px;
          overflow: hidden;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
          display: grid;
          grid-template-columns: 1fr 1fr;
          transition: transform 0.3s ease;
        }

        @media (max-width: 992px) {
          .featured-card {
            grid-template-columns: 1fr;
          }
        }

        .featured-card:hover {
          transform: translateY(-5px);
        }

        .featured-image {
          position: relative;
          overflow: hidden;
        }

        .featured-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.5s ease;
        }

        .featured-card:hover .featured-image img {
          transform: scale(1.05);
        }

        .image-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(
            to bottom,
            transparent 0%,
            rgba(10, 36, 88, 0.3) 100%
          );
          padding: 24px;
          display: flex;
          align-items: flex-end;
        }

        .project-badges {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }

        .project-badge {
          padding: 6px 12px;
          border-radius: 6px;
          font-size: 0.75rem;
          font-weight: 600;
        }

        .project-badge.category {
          background: linear-gradient(135deg, #ffd700 0%, #ffc107 100%);
          color: #0a2458;
        }

        .project-badge.industry {
          background: rgba(255, 255, 255, 0.9);
          color: #0a2458;
        }

        .featured-content {
          padding: 40px;
          display: flex;
          flex-direction: column;
          gap: 24px;
        }

        .featured-header h3 {
          font-size: 1.75rem;
          color: #0a2458;
          margin-bottom: 8px;
          line-height: 1.3;
        }

        .client-name {
          color: #0a2458;
          font-size: 0.875rem;
          margin: 0;
        }

        .project-excerpt {
          color: #0a2458;
          line-height: 1.6;
          font-size: 1rem;
        }

        .results-section h4 {
          color: #0a2458;
          margin-bottom: 12px;
          font-size: 1.125rem;
        }

        .results-grid {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .result-item {
          display: flex;
          align-items: flex-start;
          gap: 10px;
        }

        .result-icon {
          color: #10b981;
          font-size: 14px;
          margin-top: 2px;
        }

        .result-text {
          color: #0a2458;
          font-size: 0.875rem;
        }

        .testimonial-preview {
          background: #f8fafc;
          padding: 16px;
          border-radius: 12px;
          border-left: 4px solid #0a2458;
          display: flex;
          gap: 12px;
        }

        .testimonial-icon {
          font-size: 20px;
          color: #0a2458;
        }

        .testimonial-text {
          color: #0a2458;
          font-size: 0.875rem;
          margin: 0;
          font-style: italic;
        }

        .view-case-study {
          background: linear-gradient(135deg, #0a2458 0%, #1a3b8a 100%);
          color: white;
          border: none;
          padding: 16px 32px;
          border-radius: 12px;
          font-weight: 600;
          display: flex;
          align-items: center;
          justify-content: space-between;
          cursor: pointer;
          transition: all 0.3s ease;
          margin-top: auto;
        }

        .view-case-study:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 25px rgba(10, 36, 88, 0.3);
        }

        .arrow {
          font-size: 18px;
        }

        /* Projects Grid */
        .projects-grid-section {
          padding: 80px 0;
          background: white;
        }

        .projects-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
          gap: 30px;
        }

        @media (max-width: 768px) {
          .projects-grid {
            grid-template-columns: 1fr;
          }
        }

        .project-card {
          background: white;
          border-radius: 16px;
          overflow: hidden;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .project-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
        }

        .project-image {
          position: relative;
          height: 200px;
          overflow: hidden;
        }

        .project-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.5s ease;
        }

        .project-card:hover .project-image img {
          transform: scale(1.05);
        }

        .project-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(
            to bottom,
            transparent 0%,
            rgba(10, 36, 88, 0.2) 100%
          );
          padding: 16px;
          display: flex;
          align-items: flex-end;
        }

        .project-tags {
          display: flex;
          gap: 6px;
          flex-wrap: wrap;
        }

        .project-tag {
          background: rgba(255, 255, 255, 0.9);
          color: #0a2458;
          padding: 4px 10px;
          border-radius: 4px;
          font-size: 0.75rem;
          font-weight: 600;
        }

        .project-content {
          padding: 24px;
        }

        .project-header h3 {
          font-size: 1.25rem;
          color: #0a2458;
          margin-bottom: 4px;
          line-height: 1.4;
        }

        .project-client {
          color: #0a2458;
          font-size: 0.75rem;
          margin-bottom: 12px;
        }

        .project-description {
          color: #0a2458;
          font-size: 0.875rem;
          line-height: 1.5;
          margin-bottom: 16px;
        }

        .project-results h4 {
          color: #0a2458;
          font-size: 0.875rem;
          margin-bottom: 8px;
        }

        .project-results ul {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .project-results li {
          display: flex;
          align-items: flex-start;
          gap: 8px;
          color: #0a2458;
          font-size: 0.75rem;
          margin-bottom: 4px;
        }

        .result-bullet {
          color: #10b981;
          font-weight: bold;
        }

        .project-footer {
          padding-top: 16px;
          margin-top: 16px;
          border-top: 1px solid #e2e8f0;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .technologies {
          display: flex;
          gap: 6px;
          flex-wrap: wrap;
        }

        .tech-tag {
          background: #f1f5f9;
          color: #0a2458;
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 0.625rem;
        }

        .view-link {
          color: #0a2458;
          font-size: 0.875rem;
          font-weight: 600;
        }

        .no-results {
          text-align: center;
          padding: 80px 20px;
        }

        .no-results-icon {
          font-size: 64px;
          margin-bottom: 20px;
        }

        .no-results h3 {
          color: #0a2458;
          margin-bottom: 12px;
        }

        .no-results p {
          color: #0a2458;
          margin-bottom: 24px;
        }

        .reset-filters {
          background: linear-gradient(135deg, #0a2458 0%, #1a3b8a 100%);
          color: white;
          border: none;
          padding: 12px 24px;
          border-radius: 8px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .reset-filters:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 25px rgba(10, 36, 88, 0.3);
        }

        /* Technologies Section */
        .technologies-section {
          padding: 80px 0;
          background: linear-gradient(180deg, #f8faff 0%, #ffffff 100%);
        }

        .technologies-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 24px;
        }

        .tech-category {
          background: white;
          border-radius: 16px;
          padding: 24px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
          transition: transform 0.3s ease;
        }

        .tech-category:hover {
          transform: translateY(-5px);
        }

        .tech-category-header {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 20px;
        }

        .tech-icon {
          font-size: 32px;
          width: 60px;
          height: 60px;
          background: rgba(10, 36, 88, 0.1);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .tech-category h3 {
          font-size: 1.25rem;
          color: #0a2458;
          margin: 0;
        }

        .tech-items {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .tech-item {
          color: #0a2458;
          font-size: 0.875rem;
          padding: 8px 12px;
          background: #f8fafc;
          border-radius: 8px;
          transition: all 0.3s ease;
        }

        .tech-item:hover {
          background: #e6f0ff;
          color: #0a2458;
        }

        /* Industries Section */
        .industries-section {
          padding: 80px 0;
          background: white;
        }

        .industries-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 24px;
        }

        .industry-card {
          background: white;
          border-radius: 16px;
          padding: 24px;
          border: 2px solid #e2e8f0;
          cursor: pointer;
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;
        }

        .industry-card:hover {
          border-color: #0a2458;
          transform: translateY(-5px);
          box-shadow: 0 10px 30px rgba(10, 36, 88, 0.1);
        }

        .industry-icon {
          font-size: 40px;
          margin-bottom: 16px;
        }

        .industry-content {
          margin-bottom: 16px;
        }

        .industry-content h3 {
          font-size: 1.125rem;
          color: #0a2458;
          margin-bottom: 8px;
        }

        .project-count {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .count-number {
          font-size: 1.5rem;
          font-weight: 700;
          color: #ffd700;
        }

        .count-label {
          color: #0a2458;
          font-size: 0.875rem;
        }

        .industry-hover {
          position: absolute;
          bottom: -50px;
          left: 0;
          right: 0;
          padding: 16px;
          background: linear-gradient(135deg, #0a2458 0%, #1a3b8a 100%);
          color: white;
          text-align: center;
          font-weight: 600;
          transition: bottom 0.3s ease;
        }

        .industry-card:hover .industry-hover {
          bottom: 0;
        }

        /* Testimonials Section */
        .testimonials-section {
          padding: 80px 0;
          background: linear-gradient(180deg, #f8faff 0%, #ffffff 100%);
        }

        .testimonials-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
          gap: 30px;
        }

        @media (max-width: 768px) {
          .testimonials-grid {
            grid-template-columns: 1fr;
          }
        }

        .testimonial-card {
          background: white;
          border-radius: 16px;
          padding: 32px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
          transition: transform 0.3s ease;
        }

        .testimonial-card:hover {
          transform: translateY(-5px);
        }

        .testimonial-rating {
          color: #ffd700;
          font-size: 20px;
          margin-bottom: 16px;
        }

        .star {
          margin-right: 2px;
        }

        .testimonial-text {
          color: #0a2458;
          font-size: 1rem;
          line-height: 1.6;
          margin-bottom: 24px;
          font-style: italic;
        }

        .testimonial-author {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding-top: 16px;
          border-top: 1px solid #e2e8f0;
        }

        .author-info h4 {
          color: #0a2458;
          font-size: 0.875rem;
          margin-bottom: 4px;
        }

        .author-info p {
          color: #0a2458;
          font-size: 0.75rem;
          margin: 0;
        }

        .project-link span {
          background: rgba(10, 36, 88, 0.1);
          color: #0a2458;
          padding: 6px 12px;
          border-radius: 20px;
          font-size: 0.75rem;
          font-weight: 600;
        }

        /* CTA Section */
        .work-cta {
          padding: 100px 0;
          background: linear-gradient(135deg, #0a2458 0%, #1a3b8a 100%);
          position: relative;
          overflow: hidden;
        }

        .work-cta::before {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background:
            radial-gradient(
              circle at 20% 80%,
              rgba(255, 215, 0, 0.1) 0%,
              transparent 50%
            ),
            radial-gradient(
              circle at 80% 20%,
              rgba(255, 255, 255, 0.05) 0%,
              transparent 50%
            );
        }

        .cta-content {
          position: relative;
          z-index: 1;
          text-align: center;
          max-width: 800px;
          margin: 0 auto;
        }

        .cta-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(10px);
          padding: 8px 16px;
          border-radius: 50px;
          margin-bottom: 24px;
          border: 1px solid rgba(255, 255, 255, 0.2);
        }

        .cta-content h2 {
          font-size: 2.5rem;
          color: white;
          margin-bottom: 16px;
          text-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
        }

        .cta-content p {
          color: rgba(255, 255, 255, 0.9);
          font-size: 1.125rem;
          line-height: 1.6;
          margin-bottom: 40px;
        }

        .cta-buttons {
          display: flex;
          gap: 16px;
          justify-content: center;
          flex-wrap: wrap;
          margin-bottom: 60px;
        }

        .cta-primary {
          background: linear-gradient(135deg, #ffd700 0%, #ffc107 100%);
          color: #0a2458;
          border: none;
          padding: 18px 40px;
          border-radius: 12px;
          font-size: 1.125rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .cta-primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 15px 30px rgba(255, 215, 0, 0.3);
        }

        .cta-secondary {
          background: transparent;
          color: white;
          border: 2px solid #ffd700;
          padding: 18px 40px;
          border-radius: 12px;
          font-size: 1.125rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .cta-secondary:hover {
          background: rgba(255, 215, 0, 0.1);
          transform: translateY(-2px);
        }

        .cta-arrow {
          font-size: 18px;
        }

        .cta-contact {
          background: rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(10px);
          border-radius: 16px;
          padding: 32px;
          margin-top: 40px;
        }

        .contact-info {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 30px;
        }

        .contact-item {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .contact-icon {
          font-size: 24px;
          color: #ffd700;
        }

        .contact-label {
          color: rgba(255, 255, 255, 0.8);
          font-size: 0.875rem;
          margin-bottom: 4px;
        }

        .contact-value {
          color: white;
          font-weight: 600;
          font-size: 0.875rem;
        }

        /* Responsive Design */
        @media (max-width: 768px) {
          .hero-stats {
            gap: 20px;
          }

          .stat-item {
            min-width: 100px;
          }

          .stat-value {
            font-size: 2rem;
          }

          .featured-content {
            padding: 24px;
          }

          .cta-buttons {
            flex-direction: column;
          }

          .cta-primary,
          .cta-secondary {
            width: 100%;
            justify-content: center;
          }

          .contact-info {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 480px) {
          .hero-title {
            font-size: 2rem;
          }

          .hero-subtitle {
            font-size: 1rem;
          }

          .section-header h2 {
            font-size: 2rem;
          }

          .filter-options {
            flex-direction: column;
          }

          .filter-option {
            width: 100%;
            justify-content: center;
          }
        }
      `}</style>
    </div>
  );
}
